import { useEffect, useMemo, useState } from 'react';
import { Admin, type AdminCharacter, type AdminProduct } from '@/network/RestApi';
import { useUIStore } from '@/state/uiStore';
import { ui } from './theme';
import { CloseButton } from './CloseButton';

type Tab = 'diamond' | 'zen' | 'item' | 'weather';

export function AdminPanel() {
  const close = () => useUIStore.getState().set('admin', false);
  const showToast = useUIStore((s) => s.showToast);

  const [tab, setTab] = useState<Tab>('diamond');
  const [chars, setChars] = useState<AdminCharacter[]>([]);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [search, setSearch] = useState('');
  const [selChar, setSelChar] = useState<number | null>(null);
  const [amount, setAmount] = useState<number>(1000);
  const [productId, setProductId] = useState<number | null>(null);
  const [qty, setQty] = useState<number>(1);
  const [busy, setBusy] = useState(false);
  const [wMapId, setWMapId] = useState<number>(0);
  const [wKind, setWKind] = useState<'clear' | 'rain' | 'storm' | 'fog' | 'snow'>('clear');
  const [wDuration, setWDuration] = useState<number>(600);

  const reloadChars = (q = search) => {
    Admin.characters(q)
      .then((r) => setChars(r.characters ?? []))
      .catch((e) => showToast((e as Error).message, 'error'));
  };

  useEffect(() => {
    reloadChars('');
    Admin.products()
      .then((r) => setProducts(r.products ?? []))
      .catch((e) => showToast((e as Error).message, 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(() => chars.find((c) => c.id === selChar) ?? null, [chars, selChar]);

  const submit = async () => {
    if (tab === 'weather') {
      setBusy(true);
      try {
        await Admin.setWeather({ mapId: wMapId, weather: wKind, durationSec: wDuration });
        showToast(`Weather → ${wKind} (map ${wMapId === 0 ? 'ALL' : wMapId}) for ${wDuration}s`);
      } catch (e) {
        showToast((e as Error).message, 'error');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!selected) { showToast('Chọn nhân vật trước', 'warn'); return; }
    setBusy(true);
    try {
      if (tab === 'diamond') {
        const r = await Admin.giveDiamond({ accountId: selected.accountId, amount });
        showToast(`+${amount} Kim Cương → ${selected.username} (now ${r.diamond})`);
      } else if (tab === 'zen') {
        const r = await Admin.giveZen({ characterId: selected.id, amount });
        showToast(`+${amount} Zen → ${selected.name} (now ${r.zen})`);
      } else {
        if (!productId) { showToast('Chọn sản phẩm', 'warn'); return; }
        const r = await Admin.giveItem({ characterId: selected.id, productId, quantity: qty });
        showToast(`Tặng item tpl#${r.itemTemplateId} ×${r.quantity} → ${selected.name}`);
      }
      reloadChars();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: 'absolute', top: 70, left: '50%', transform: 'translateX(-50%)',
      width: 720, maxHeight: 560, overflow: 'auto', zIndex: 1000, ...ui.panel,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong>Admin Panel</strong>
        <CloseButton onClick={close} />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
            <input
              style={ui.input}
              placeholder="Tìm theo tên nhân vật / username"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') reloadChars(); }}
            />
            <button style={ui.secondaryBtn} onClick={() => reloadChars()}>Tìm</button>
          </div>
          <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid #324a5e', borderRadius: 4 }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead style={{ background: '#0b1220', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={th}>ID</th>
                  <th style={th}>User</th>
                  <th style={th}>Char</th>
                  <th style={th}>Lv</th>
                  <th style={th}>◆</th>
                  <th style={th}>Z</th>
                </tr>
              </thead>
              <tbody>
                {chars.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setSelChar(c.id)}
                    style={{
                      cursor: 'pointer',
                      background: selChar === c.id ? '#1f3142' : 'transparent',
                    }}
                  >
                    <td style={td}>{c.id}</td>
                    <td style={td}>{c.username}</td>
                    <td style={td}>{c.name} <span style={{ opacity: 0.6 }}>({c.class})</span></td>
                    <td style={td}>{c.level}</td>
                    <td style={td}>{c.diamond}</td>
                    <td style={td}>{c.zen}</td>
                  </tr>
                ))}
                {chars.length === 0 && (
                  <tr><td style={td} colSpan={6}>Không có dữ liệu</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ width: 340 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
            {(['diamond', 'zen', 'item', 'weather'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  ...ui.secondaryBtn,
                  background: tab === t ? '#0b1220' : '#324a5e',
                  fontWeight: tab === t ? 700 : 400,
                }}
              >
                {t === 'diamond' ? '◆ Diamond' : t === 'zen' ? 'Z Zen' : t === 'item' ? 'Item' : 'Weather'}
              </button>
            ))}
          </div>

          <div style={{
            background: '#0b1220', border: '1px solid #324a5e',
            padding: 10, borderRadius: 4, marginBottom: 8, fontSize: 12,
          }}>
            <div style={{ opacity: 0.7 }}>Đã chọn:</div>
            {selected ? (
              <div>
                <strong>{selected.name}</strong> · acc <strong>{selected.username}</strong> #{selected.accountId}<br />
                Lv {selected.level} · ◆ {selected.diamond} · Z {selected.zen}
              </div>
            ) : <div style={{ opacity: 0.6 }}>Chọn 1 nhân vật trong bảng bên trái</div>}
          </div>

          {(tab === 'diamond' || tab === 'zen') && (
            <label style={{ fontSize: 12 }}>
              Số lượng (âm để trừ):
              <input
                type="number"
                style={{ ...ui.input, marginTop: 4 }}
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value || '0', 10))}
              />
            </label>
          )}

          {tab === 'item' && (
            <>
              <label style={{ fontSize: 12 }}>
                Chọn sản phẩm (shop_products):
                <select
                  style={{ ...ui.input, marginTop: 4 }}
                  value={productId ?? ''}
                  onChange={(e) => setProductId(e.target.value ? parseInt(e.target.value, 10) : null)}
                >
                  <option value="">-- chọn --</option>
                  {products.filter((p) => p.itemTemplateId).map((p) => (
                    <option key={p.id} value={p.id}>
                      #{p.id} · {p.name} (tpl {p.itemTemplateId} ×{p.quantity})
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                Số lần (1-99):
                <input
                  type="number"
                  min={1}
                  max={99}
                  style={{ ...ui.input, marginTop: 4 }}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(99, parseInt(e.target.value || '1', 10))))}
                />
              </label>
            </>
          )}

          {tab === 'weather' && (
            <>
              <label style={{ fontSize: 12, display: 'block' }}>
                Map ID (0 = tất cả):
                <input
                  type="number"
                  min={0}
                  style={{ ...ui.input, marginTop: 4 }}
                  value={wMapId}
                  onChange={(e) => setWMapId(Math.max(0, parseInt(e.target.value || '0', 10)))}
                />
              </label>
              <label style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                Thời tiết:
                <select
                  style={{ ...ui.input, marginTop: 4 }}
                  value={wKind}
                  onChange={(e) => setWKind(e.target.value as typeof wKind)}
                >
                  <option value="clear">Clear (Quang đãng)</option>
                  <option value="rain">Rain (Mưa)</option>
                  <option value="storm">Storm (Bão)</option>
                  <option value="fog">Fog (Sương mù)</option>
                  <option value="snow">Snow (Tuyết)</option>
                </select>
              </label>
              <label style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                Thời gian giữ (giây, max 86400):
                <input
                  type="number"
                  min={1}
                  max={86400}
                  style={{ ...ui.input, marginTop: 4 }}
                  value={wDuration}
                  onChange={(e) => setWDuration(Math.max(1, Math.min(86400, parseInt(e.target.value || '1', 10))))}
                />
              </label>
            </>
          )}

          <button
            style={{ ...ui.primaryBtn, marginTop: 12, width: '100%' }}
            disabled={busy || (tab !== 'weather' && !selected)}
            onClick={submit}
          >
            {busy ? 'Đang xử lý...' : 'Thực thi'}
          </button>
        </div>
      </div>
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #324a5e' };
const td: React.CSSProperties = { padding: '4px 6px', borderBottom: '1px solid #1a2535' };
